/* ============================================================
   ProviderReferralPanel — 2f9c70ba cabinet card: referral link
   (?ref= code), vanity shop slug reservation, aggregate stats.
   Mounted on the portal profile tab (sibling of the payout card).
   ============================================================ */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { ProviderReferral } from '../types/referral';
import {
  buildReferralLink,
  normalizeSlugInput,
  referralTotals,
  validateSlugLocal,
} from './referralView';

const SLUG_LIMITS = { min: 3, max: 32 };

export function ProviderReferralPanel() {
  const [referral, setReferral] = useState<ProviderReferral | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getProviderReferral();
      setReferral(r);
      setSlugInput(r.shopSlug ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)' }}>{error}</p>
        <button className="btn btn-secondary" onClick={load} style={{ marginTop: 'var(--space-sm)' }}>
          Повторить
        </button>
      </div>
    );
  }

  const link = buildReferralLink(window.location.origin, referral.referralCode);
  const slugError = validateSlugLocal(slugInput, SLUG_LIMITS);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setNote('Не удалось скопировать — скопируйте ссылку вручную.');
    }
  };

  const saveSlug = async (e: FormEvent) => {
    e.preventDefault();
    if (saving || slugError) return;
    setSaving(true);
    setNote(null);
    try {
      const next = await api.setShopSlug(normalizeSlugInput(slugInput));
      setReferral(next);
      setSlugInput(next.shopSlug ?? '');
      setNote(next.shopSlug ? 'Адрес магазина сохранён.' : 'Адрес освобождён.');
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Не удалось сохранить адрес');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (saving) return;
    setSaving(true);
    setNote(null);
    try {
      const next = await api.regenerateReferralCode();
      setReferral(next);
      setNote('Код обновлён — старые ссылки перестанут работать.');
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Не удалось обновить код');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Реферальная программа</p>

      {/* Referral link */}
      <div>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Ваша ссылка — приглашённые клиенты видят вас первым при выборе исполнителя
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', marginTop: 'var(--space-xs)' }}>
          <input
            readOnly
            value={link}
            onFocus={(e) => e.currentTarget.select()}
            style={{ flex: 1, fontSize: 'var(--fs-caption)', padding: 'var(--space-xs) var(--space-sm)' }}
          />
          <button type="button" className="btn btn-secondary" onClick={copyLink}>
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={regenerate} disabled={saving}>
            Новый код
          </button>
        </div>
      </div>

      {/* Vanity slug */}
      <form onSubmit={saveSlug}>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Адрес магазина (example.com/r/&lt;адрес&gt;; в будущем — поддомен)
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', marginTop: 'var(--space-xs)' }}>
          <input
            value={slugInput}
            onChange={(e) => setSlugInput(normalizeSlugInput(e.target.value))}
            placeholder="my-shop"
            maxLength={64}
            style={{ flex: 1, fontSize: 'var(--fs-caption)', padding: 'var(--space-xs) var(--space-sm)' }}
          />
          <button type="submit" className="btn btn-primary" disabled={saving || slugError !== null}>
            {slugInput ? 'Сохранить' : 'Освободить'}
          </button>
        </div>
        {slugError && (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', marginTop: 'var(--space-xs)' }}>
            {slugError}
          </p>
        )}
      </form>

      {/* Stats */}
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        Переходов: {referral.visitCount} · Привлечено клиентов: {referral.attributedCustomers} ·{' '}
        {referralTotals(referral)} (учёт, без выплат)
      </p>

      {note && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-primary)' }}>{note}</p>
      )}
    </div>
  );
}
